import { AudioClip, AudioSource, Game, game, sys } from 'cc';
import BrowserChecker from '../../Utils/BrowserChecker';
import Delay from '../../Utils/Delay';

declare module 'cc' {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface AudioSource {
        isLoadingClip: boolean;
        usedClip: AudioClip;
    }
}

type DeferredActionType = {
    createdAt: number;
    action: (elapsed: number) => void;
};
function addDeferredAction(action: (elapsed: number) => void): void {
    deferredActionList.push({
        createdAt: Date.now(),
        action,
    });
}
const deferredActionList: DeferredActionType[] = [];
const browserInfo = BrowserChecker.check({ isDesktopSiteMode: true });
const isIOS26SafariDesktopSiteMode = browserInfo.isSupported && browserInfo.name === 'safari' && browserInfo.version === 26;

let isContextRunning = false;
let attemptToStart = false;
let startAudioSource: AudioSource = null;
let waitContextRunning: Promise<void> = Promise.resolve();
let hasEnteredBackground = false;

if (sys.os === sys.OS.IOS) {
    game.on(Game.EVENT_SHOW, () => {
        isContextRunning = false;
        attemptToStart = false;
        startAudioSource = null;
    });
    game.once(Game.EVENT_HIDE, () => {
        hasEnteredBackground = true;
    });
}

Object.defineProperty(AudioSource.prototype, 'isLoadingClip', {
    configurable: true,
    get: function get(this: AudioSource) {
        return (this as any)._isLoaded && !!this.clip;
    },
});

const canvas = document.getElementById('GameCanvas') as HTMLCanvasElement;

/**
 * iOS 26 website mode may delay audio recovery after returning to foreground.
 * Wait for either a user gesture or a 10-second timeout.
 */
async function waitForIOS26WebsiteModeContextRunning(): Promise<void> {
    await Promise.race([
        new Promise<void>((resolve) => {
            canvas?.addEventListener('touchend', () => resolve(), {
                capture: true,
                once: true,
            });
        }),
        Delay.resolve(10000),
    ]);
}

const originalPlay = AudioSource.prototype.play;
AudioSource.prototype.play = async function play(this: AudioSource) {
    if (isContextRunning) {
        originalPlay.call(this);
        return;
    }

    if (!attemptToStart) {
        // if context is not running and has not attempted to start, start it
        attemptToStart = true;
        startAudioSource = this;

        if (isIOS26SafariDesktopSiteMode && hasEnteredBackground) {
            waitContextRunning = waitForIOS26WebsiteModeContextRunning();
        }
        await waitContextRunning;

        this.node.once(AudioSource.EventType.STARTED, async () => {
            isContextRunning = true;
            deferredActionList.forEach(({ action, createdAt }) => action(Date.now() - createdAt));
            deferredActionList.length = 0;
        });
        originalPlay.call(this);
    } else if (this === startAudioSource) {
        await waitContextRunning;
        // if context is not running and has attempted to start and its same audio source, start it again
        originalPlay.call(this);
    } else {
        // if context is not running and has attempted to start and its not same audio source, defer it
        addDeferredAction((elapsed) => {
            originalPlay.call(this);
            // set currentTime to elapsed time since the action was created
            this.currentTime = elapsed / 1000;
        });
    }
};

const originalStop = AudioSource.prototype.stop;
const checkInterruptBeforeStop = async function checkInterruptBeforeStop(this: AudioSource) {
    // if audio source is interrupted, play it again before invoking stop
    // because stop can't function correctly when audio source is interrupted
    if (this.state === AudioSource.AudioState.INTERRUPTED) {
        this.play();
    }
    originalStop.call(this);
};
AudioSource.prototype.stop = function stop(this: AudioSource) {
    if (isContextRunning) {
        checkInterruptBeforeStop.call(this);
    } else {
        addDeferredAction(() => checkInterruptBeforeStop.call(this));
    }
};

const originalPause = AudioSource.prototype.pause;
AudioSource.prototype.pause = function pause(this: AudioSource) {
    if (isContextRunning) {
        originalPause.call(this);
    } else {
        addDeferredAction(() => originalPause.call(this));
    }
};

const originalCurrentTimeDescriptor = Object.getOwnPropertyDescriptor(AudioSource.prototype, 'currentTime');
const originCurrentTimeSetter = originalCurrentTimeDescriptor.set;
originalCurrentTimeDescriptor.set = function set(this: AudioSource, value: number) {
    if (isContextRunning) {
        originCurrentTimeSetter.call(this, value);
    } else {
        addDeferredAction((elapsed) => {
            // set currentTime to value + elapsed time since the action was created
            originCurrentTimeSetter.call(this, value + elapsed / 1000);
        });
    }
};
Object.defineProperty(AudioSource.prototype, 'currentTime', originalCurrentTimeDescriptor);

const originalClipDescriptor = Object.getOwnPropertyDescriptor(AudioSource.prototype, 'clip');
const originClipSetter = originalClipDescriptor.set;
originalClipDescriptor.set = function set(this: AudioSource, value: AudioClip) {
    this.usedClip = value;
    // allow to set clip when context is running or has not attempted to start
    if (isContextRunning || !attemptToStart) {
        originClipSetter.call(this, value);
    } else {
        addDeferredAction(() => {
            if (this.isValid) {
                originClipSetter.call(this, value);
            }
        });
    }
};
Object.defineProperty(AudioSource.prototype, 'clip', originalClipDescriptor);
